import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useTrip } from '../contexts/TripContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { DestinationVoting } from '../components/trip/DestinationVoting';
import { ActivityTimeline } from '../components/trip/ActivityTimeline';
import { TransparencySettings } from '../components/trip/TransparencySettings';
import { RemindersManager } from '../components/trip/RemindersManager';
import { TripTutorial, TripTutorialButton, useTripTutorial } from '../components/trip/TripTutorial';
import { ScreenHelp } from '../components/common/ScreenHelp';
import { copyToClipboard } from '../utils/clipboard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  MapPin, Calendar, Users, CreditCard, ShoppingBag, Car, 
  MessageCircle, Vote, FileText, Settings, Share2, Copy, 
  CheckCircle2, Loader2, Crown, ArrowLeft, Download, ShoppingCart, Image, BarChart3, ClipboardCheck, Star, X, Plane, Camera, Trash2,
  Sun, Cloud, CloudRain, Thermometer, Droplets, Wind, Plus, Home, UserMinus, HelpCircle, Sparkles, ListTodo
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import TripGeneralSettings from '../components/TripGeneralSettings';

// Weather icons mapping
const weatherIcons = {
  '01d': Sun, '01n': Sun,
  '02d': Cloud, '02n': Cloud,
  '03d': Cloud, '03n': Cloud,
  '04d': Cloud, '04n': Cloud,
  '09d': CloudRain, '09n': CloudRain,
  '10d': CloudRain, '10n': CloudRain,
  '11d': CloudRain, '11n': CloudRain,
  '13d': Cloud, '13n': Cloud,
  '50d': Wind, '50n': Wind,
};

// Default banner images based on destination keywords
const getBannerImage = (destination) => {
  const dest = destination?.toLowerCase() || '';
  if (dest.includes('praia') || dest.includes('beach') || dest.includes('litoral')) {
    return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&h=400&fit=crop';
  }
  if (dest.includes('montanha') || dest.includes('serra') || dest.includes('campo')) {
    return 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&h=400&fit=crop';
  }
  if (dest.includes('cidade') || dest.includes('são paulo') || dest.includes('rio')) {
    return 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1200&h=400&fit=crop';
  }
  if (dest.includes('paraty') || dest.includes('búzios') || dest.includes('angra')) {
    return 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=1200&h=400&fit=crop';
  }
  // Default travel image
  return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&h=400&fit=crop';
};

const statusConfig = {
  planning: { label: 'Planejando', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  voting: { label: 'Votando destino', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  confirmed: { label: 'Confirmada', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  in_progress: { label: 'Em andamento', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'Concluída', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const menuItems = [
  { path: '', icon: Home, label: 'Início' },
  { path: 'my-trip', icon: Users, label: 'Minha Viagem' },
  { path: 'itinerary', icon: Calendar, label: 'Roteiro' },
  { path: 'payments', icon: CreditCard, label: 'Pagamentos' },
  { path: 'members', icon: Users, label: 'Membros' },
  { path: 'tasks', icon: ListTodo, label: 'Lembretes' },
  { path: 'gallery', icon: Image, label: 'Galeria' },
  { path: 'carpools', icon: Car, label: 'Caronas' },
  { path: 'shopping', icon: ShoppingCart, label: 'Listas' },
  { path: 'votes', icon: Vote, label: 'Votações' },
  { path: 'faq', icon: HelpCircle, label: 'Dúvidas' },
  { path: 'checklist', icon: ClipboardCheck, label: 'Checklist' },
  { path: 'withdrawals', icon: UserMinus, label: 'Desistências' },
  { path: 'feedback', icon: MessageCircle, label: 'Feedback' },
  { path: 'reviews', icon: Star, label: 'Avaliações', completedOnly: true },
  { path: 'analytics', icon: BarChart3, label: 'Analytics', organizerOnly: true },
];

export default function TripDetail() {
  const { tripId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { currentTrip, fetchTrip, fetchMembers, members, loading, uploadCoverImage, removeCoverImage } = useTrip();
  const [copied, setCopied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [coverImageDialogOpen, setCoverImageDialogOpen] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [isMenuSticky, setIsMenuSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTripTutorial, setShowTripTutorial] = useState(false);
  const [myTripInfo, setMyTripInfo] = useState(null);
  const fileInputRef = React.useRef(null);
  const menuRef = useRef(null);
  const menuPlaceholderRef = useRef(null);
  const lastScrollY = useRef(0);
  const { api } = useAuth();

  // Check if user is a member of this trip
  const currentUserMember = members?.find(m => m.user_id === user?.id);
  const isMember = currentUserMember?.status === 'confirmed';
  const isOrganizer = currentUserMember?.role === 'organizer' || currentTrip?.my_role === 'organizer';

  // Fetch my trip info to check completion status
  useEffect(() => {
    const fetchMyTripInfo = async () => {
      if (!tripId || !isMember) return;
      try {
        const response = await api.get(`/trips/${tripId}/member-info/me`);
        setMyTripInfo(response.data);
      } catch (error) {
        // User might not have trip info yet
        setMyTripInfo(null);
      }
    };
    fetchMyTripInfo();
  }, [tripId, isMember, api]);

  // Check if my-trip needs attention
  const myTripNeedsAttention = !myTripInfo?.is_completed && myTripInfo?.is_traveling !== false;

  // Helper function to get pending count for a menu item
  const getPendingCount = (path) => {
    if (!currentTrip) return 0;
    switch (path) {
      case 'members':
        return isOrganizer ? (currentTrip.pending_requests_count || 0) : 0;
      case 'payments':
        return currentTrip.pending_payments_count || 0;
      case 'tasks':
        return currentTrip.pending_tasks_count || 0;
      case 'withdrawals':
        return isOrganizer ? (currentTrip.pending_withdrawals_count || 0) : 0;
      default:
        return 0;
    }
  };

  // Determine tutorial type based on trip status
  const isVotingMode = currentTrip?.status === 'voting' || currentTrip?.destination_voting?.is_active;
  const tutorialType = isVotingMode ? 'voting' : 'regular';

  // Check if tutorial should show on first visit
  useEffect(() => {
    if (!tripId || !currentTrip) return;
    
    // Check if this trip was just created (from sessionStorage)
    const justCreated = sessionStorage.getItem(`trip_just_created_${tripId}`);
    if (justCreated === 'true') {
      // Check if user hasn't disabled this tutorial type
      const tutorialHidden = localStorage.getItem(`trip_tutorial_${tutorialType}_hidden`);
      if (tutorialHidden !== 'true') {
        // Small delay to let the page render
        let isMounted = true;
        const showTutorial = async () => {
          await new Promise(resolve => setTimeout(resolve, 800));
          if (isMounted) {
            setShowTripTutorial(true);
            sessionStorage.removeItem(`trip_just_created_${tripId}`);
          }
        };
        showTutorial();
        return () => { isMounted = false; };
      } else {
        sessionStorage.removeItem(`trip_just_created_${tripId}`);
      }
    }
  }, [tripId, currentTrip, tutorialType]);

  // Sticky menu logic - same menu becomes fixed when scrolled past
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const placeholderElement = menuPlaceholderRef.current;
      
      if (!placeholderElement) return;
      
      const placeholderRect = placeholderElement.getBoundingClientRect();
      const headerHeight = 64; // Header height
      
      // Menu should be sticky when its original position goes above the header
      const shouldBeSticky = placeholderRect.top < headerHeight;
      
      // Only unstick when scrolling UP and the original position is visible again
      if (shouldBeSticky) {
        setIsMenuSticky(true);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up and original position is back in view
        setIsMenuSticky(false);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch weather forecast when modal opens
  const fetchForecast = useCallback(async () => {
    if (!tripId || forecast) return;
    setForecastLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/live/forecast`);
      setForecast(response.data);
    } catch (error) {
      console.log('Forecast not available:', error);
    } finally {
      setForecastLoading(false);
    }
  }, [tripId, api, forecast]);

  // Fetch forecast when modal opens
  useEffect(() => {
    if (weatherModalOpen && !forecast) {
      fetchForecast();
    }
  }, [weatherModalOpen, forecast, fetchForecast]);

  // Fetch live data (weather + countdown)
  const fetchLiveData = useCallback(async () => {
    if (!tripId) return;
    try {
      const response = await api.get(`/trips/${tripId}/live`);
      setLiveData(response.data);
      if (response.data?.weather) {
        setWeather(response.data.weather);
      }
      if (response.data?.countdown) {
        setCountdown({
          days: response.data.countdown.days || 0,
          hours: response.data.countdown.hours || 0,
          minutes: response.data.countdown.minutes || 0,
          seconds: 0,
        });
      }
    } catch (error) {
      console.log('Live data not available');
    }
  }, [tripId, api]);

  // Calculate countdown based on trip start date
  useEffect(() => {
    if (!currentTrip?.start_date) return;
    
    const calculateCountdown = () => {
      const startDate = typeof currentTrip.start_date === 'string' 
        ? parseISO(currentTrip.start_date) 
        : currentTrip.start_date;
      
      const now = new Date();
      const diff = startDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    };
    
    // Initial calculation
    setCountdown(calculateCountdown());
    
    // Update every second
    const timer = setInterval(() => {
      setCountdown(calculateCountdown());
    }, 1000);

    return () => clearInterval(timer);
  }, [currentTrip?.start_date]);

  useEffect(() => {
    if (tripId) {
      fetchTrip(tripId);
      fetchMembers(tripId);
      fetchLiveData();
    }
  }, [tripId, fetchTrip, fetchMembers, fetchLiveData]);

  const handleCopyCode = async () => {
    if (currentTrip?.invite_code) {
      const success = await copyToClipboard(currentTrip.invite_code);
      if (success) {
        setCopied(true);
        toast.success('Código copiado!');
        await new Promise(resolve => setTimeout(resolve, 2000));
        setCopied(false);
      } else {
        toast.error('Erro ao copiar código');
      }
    }
  };

  // Public URL for invite links
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;

  const handleShare = async () => {
    if (currentTrip) {
      const joinUrl = `${PUBLIC_URL}/join/${currentTrip.invite_code}`;
      const shareData = {
        title: `Viagem: ${currentTrip.name}`,
        text: `Você foi convidado para a viagem "${currentTrip.name}" para ${currentTrip.destination}! Clique no link para participar:`,
        url: joinUrl,
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          // Copy link to clipboard as fallback
          const success = await copyToClipboard(joinUrl);
          if (success) {
            toast.success('Link copiado!');
          } else {
            toast.error('Erro ao copiar link');
          }
        }
      } else {
        const success = await copyToClipboard(joinUrl);
        if (success) {
          toast.success('Link copiado!');
        } else {
          toast.error('Erro ao copiar link');
        }
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viagem_${currentTrip.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exportado!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG ou WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 5MB');
      return;
    }

    setUploadingCover(true);
    try {
      await uploadCoverImage(tripId, file);
      toast.success('Imagem de capa atualizada! 📸');
      setCoverImageDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingCover(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveCoverImage = async () => {
    setUploadingCover(true);
    try {
      await removeCoverImage(tripId);
      toast.success('Imagem de capa removida');
      setCoverImageDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao remover imagem');
    } finally {
      setUploadingCover(false);
    }
  };

  if (loading && !currentTrip) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!currentTrip) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">{t('errors.notFound')}</p>
          <Link to="/dashboard">
            <Button className="mt-4 rounded-full">{t('tripDetail.goToDashboard')}</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const status = statusConfig[currentTrip.status] || statusConfig.planning;
  const startDate = currentTrip.start_date ? (typeof currentTrip.start_date === 'string' ? parseISO(currentTrip.start_date) : currentTrip.start_date) : null;
  const endDate = currentTrip.end_date ? (typeof currentTrip.end_date === 'string' ? parseISO(currentTrip.end_date) : currentTrip.end_date) : null;

  const currentPath = location.pathname.split('/').pop();
  const activeTab = menuItems.find(item => item.path === currentPath)?.path || '';

  return (
    <DashboardLayout tripId={tripId}>
      <div className="space-y-6">
        {/* Back Button */}
        <Link to="/dashboard" className="inline-flex items-center text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Link>

        {/* Hero Banner - Facebook Style */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="overflow-hidden border-0 shadow-lg">
            {/* Banner Image with Overlay */}
            <div className="relative h-48 sm:h-64 lg:h-72 group">
              <img
                src={currentTrip.cover_image || getBannerImage(currentTrip.destination)}
                alt={currentTrip.destination}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Edit Cover Button (Organizer only) - Mobile: bottom-center, Desktop: top-right on hover */}
              {isOrganizer && (
                <Dialog open={coverImageDialogOpen} onOpenChange={setCoverImageDialogOpen}>
                  <DialogTrigger asChild>
                    <button 
                      className="absolute bottom-20 right-4 sm:bottom-auto sm:top-4 sm:right-28 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-black/50 hover:bg-black/70 text-white p-2 sm:px-3 sm:py-2 rounded-full flex items-center gap-2 text-sm backdrop-blur-sm"
                      data-testid="edit-cover-btn"
                    >
                      <Camera className="w-4 h-4" />
                      <span className="hidden sm:inline">Editar capa</span>
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Imagem de capa da viagem</DialogTitle>
                      <DialogDescription>
                        Escolha uma foto para personalizar o banner da sua viagem.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Current image preview */}
                      <div className="relative h-32 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                        <img
                          src={currentTrip.cover_image || getBannerImage(currentTrip.destination)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        {currentTrip.cover_image && (
                          <Badge className="absolute top-2 left-2 bg-green-500 text-white">
                            Foto personalizada
                          </Badge>
                        )}
                      </div>
                      
                      {/* Upload button */}
                      <div className="space-y-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleCoverImageUpload}
                          className="hidden"
                          id="cover-image-input"
                        />
                        <Button
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingCover}
                        >
                          {uploadingCover ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Camera className="w-4 h-4 mr-2" />
                          )}
                          Escolher nova foto
                        </Button>
                        
                        {currentTrip.cover_image && (
                          <Button
                            variant="outline"
                            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={handleRemoveCoverImage}
                            disabled={uploadingCover}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Usar imagem padrão
                          </Button>
                        )}
                      </div>
                      
                      <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                        JPG, PNG ou WebP • Máximo 5MB
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              
              {/* Status Badge - Top Right */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <Badge className={`${status.color} shadow-lg`}>{status.label}</Badge>
                {isOrganizer && (
                  <div className="bg-orange-500 text-white p-1.5 rounded-full shadow-lg">
                    <Crown className="w-4 h-4" />
                  </div>
                )}
              </div>

              {/* Countdown - Top Left (for upcoming trips) - Hidden on mobile */}
              {currentTrip.status !== 'completed' && startDate && (() => {
                const daysLeft = differenceInDays(startDate, new Date());
                // Trip already started or completed
                if (daysLeft < 0) {
                  return (
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="absolute top-4 left-4 z-10 hidden sm:block"
                      data-testid="countdown-zero"
                    >
                      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-xl">
                        <div className="flex items-center gap-1.5 sm:gap-3">
                          {[
                            { value: 0, label: 'd' },
                            { value: 0, label: 'h' },
                            { value: 0, label: 'm' },
                            { value: 0, label: 's' },
                          ].map((item, i) => (
                            <div key={i} className="text-center">
                              <span className="block text-base sm:text-2xl font-bold text-slate-400 dark:text-slate-500 font-mono">
                                {String(item.value).padStart(2, '0')}
                              </span>
                              <span className="text-[8px] sm:text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                {item.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  );
                }
                // Trip starts today
                if (daysLeft === 0 && countdown.hours === 0 && countdown.minutes === 0 && countdown.seconds === 0) {
                  return (
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="absolute top-4 left-4 z-10 hidden sm:block"
                      data-testid="countdown-today"
                    >
                      <div className="bg-green-500 text-white rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 shadow-xl animate-pulse">
                        <div className="flex items-center gap-2">
                          <Plane className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="font-bold text-sm sm:text-lg">HORA DE VIAJAR!</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                }
                // Countdown is active
                return (
                  <motion.div 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="absolute top-4 left-4 z-10 hidden sm:block"
                    data-testid="countdown-active"
                  >
                    <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-xl">
                      <div className="flex items-center gap-1.5 sm:gap-3">
                        {[
                          { value: countdown.days, label: 'd' },
                          { value: countdown.hours, label: 'h' },
                          { value: countdown.minutes, label: 'm' },
                          { value: countdown.seconds, label: 's' },
                        ].map((item, i) => (
                          <div key={i} className="text-center">
                            <span className="block text-base sm:text-2xl font-bold text-orange-500 font-mono">
                              {String(item.value).padStart(2, '0')}
                            </span>
                            <span className="text-[8px] sm:text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {item.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })()}

              {/* Weather Widget - Clickable to open forecast modal - Hidden on mobile */}
              {weather && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="absolute top-4 left-56 z-10 hidden sm:block"
                >
                  <button
                    onClick={() => setWeatherModalOpen(true)}
                    className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-xl flex items-center gap-1.5 sm:gap-2 hover:bg-white dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    data-testid="weather-widget-btn"
                  >
                    {(() => {
                      const WeatherIcon = weather.icon ? (weatherIcons[weather.icon] || Sun) : Sun;
                      return <WeatherIcon className="w-5 h-5 sm:w-6 sm:h-6 text-sky-500" />;
                    })()}
                    <div className="text-left">
                      <span className="text-sm sm:text-lg font-bold text-slate-900 dark:text-white">{weather.temperature}°</span>
                      <span className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 block leading-tight">{weather.city}</span>
                    </div>
                  </button>
                </motion.div>
              )}

              {/* Weather Forecast Modal */}
              <Dialog open={weatherModalOpen} onOpenChange={setWeatherModalOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                        <Thermometer className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                      </div>
                      <div>
                        <span className="block">Clima em {forecast?.city || weather?.city || currentTrip.destination}</span>
                        <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                          Previsão para a viagem
                        </span>
                      </div>
                    </DialogTitle>
                    <DialogDescription>
                      Previsão meteorológica para o período da sua viagem
                    </DialogDescription>
                  </DialogHeader>
                  
                  {forecastLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                    </div>
                  ) : forecast?.current || weather ? (
                    <div className="space-y-4 pt-2">
                      {/* Current Weather */}
                      <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-4 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm opacity-80">Agora</p>
                            <p className="text-5xl font-bold">{forecast?.current?.temperature || weather?.temperature}°</p>
                            <p className="text-sm capitalize">{forecast?.current?.description || weather?.description || 'Parcialmente nublado'}</p>
                          </div>
                          <div className="text-right">
                            {(() => {
                              const iconCode = forecast?.current?.icon || weather?.icon;
                              const WeatherIcon = iconCode ? (weatherIcons[iconCode] || Sun) : Sun;
                              return <WeatherIcon className="w-16 h-16 opacity-90" />;
                            })()}
                          </div>
                        </div>
                        <p className="text-sm opacity-80 mt-2">
                          Sensação térmica: {forecast?.current?.feels_like || weather?.feels_like || weather?.temperature}°
                        </p>
                      </div>

                      {/* Weather Details */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                            <Droplets className="w-5 h-5 text-sky-500" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Umidade</p>
                            <p className="font-semibold text-slate-900 dark:text-white">{forecast?.current?.humidity || weather?.humidity || 65}%</p>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                            <Wind className="w-5 h-5 text-sky-500" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Vento</p>
                            <p className="font-semibold text-slate-900 dark:text-white">{forecast?.current?.wind_speed || weather?.wind_speed || 12} km/h</p>
                          </div>
                        </div>
                      </div>

                      {/* Daily Forecast */}
                      {forecast?.daily && forecast.daily.length > 0 && (
                        <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              Próximos dias
                            </h4>
                            {forecast.is_mocked && (
                              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                Simulado
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-2">
                            {forecast.daily.map((day, i) => {
                              const ForecastIcon = weatherIcons[day.icon] || Sun;
                              return (
                                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                                  <div className="flex items-center gap-3">
                                    <ForecastIcon className="w-5 h-5 text-sky-500" />
                                    <div>
                                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {day.day_name}
                                      </span>
                                      <span className="text-xs text-slate-400 ml-2">
                                        {day.date.split('-').slice(1).reverse().join('/')}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className="font-semibold text-slate-900 dark:text-white">{day.temp_max}°</span>
                                    <span className="text-slate-400">/</span>
                                    <span className="text-slate-500 dark:text-slate-400">{day.temp_min}°</span>
                                    {day.pop > 0.3 && (
                                      <span className="text-sky-500 text-xs ml-1">
                                        💧{Math.round(day.pop * 100)}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Disclaimer */}
                      <p className="text-xs text-slate-400 dark:text-slate-500 text-center pt-2">
                        {forecast?.is_mocked 
                          ? '* Dados simulados. Configure OPENWEATHER_API_KEY para previsão real.'
                          : '* Previsões podem variar. Dados via OpenWeatherMap.'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Cloud className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Dados de clima não disponíveis</p>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Main Content - Bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
                  {/* Trip Info */}
                  <div className="flex-1">
                    <h1 className="font-heading font-bold text-xl sm:text-3xl lg:text-4xl text-white drop-shadow-lg mb-2">
                      {currentTrip.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-white/90">
                      <div className="flex items-center gap-1 sm:gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2 sm:px-3 py-0.5 sm:py-1">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{currentTrip.destination}</span>
                      </div>
                      {startDate && endDate && (
                        <div className="flex items-center gap-1 sm:gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2 sm:px-3 py-0.5 sm:py-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm font-medium">
                            {format(startDate, "dd MMM", { locale: ptBR })} - {format(endDate, "dd MMM", { locale: ptBR })}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 sm:gap-1.5 bg-black/30 backdrop-blur-sm rounded-full px-2 sm:px-3 py-0.5 sm:py-1">
                        <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{currentTrip.member_count}</span>
                      </div>
                      {/* Mobile countdown badge */}
                      {currentTrip.status !== 'completed' && startDate && countdown.days >= 0 && (
                        <div className="flex items-center gap-1 bg-orange-500 backdrop-blur-sm rounded-full px-2 py-0.5 sm:hidden">
                          <span className="text-xs font-bold text-white">
                            {countdown.days > 0 ? `${countdown.days}d ${countdown.hours}h` : countdown.hours > 0 ? `${countdown.hours}h ${countdown.minutes}m` : `${countdown.minutes}m`}
                          </span>
                        </div>
                      )}
                      {/* Mobile weather badge */}
                      {weather && (
                        <button 
                          onClick={() => setWeatherModalOpen(true)}
                          className="flex items-center gap-1 bg-sky-500/80 backdrop-blur-sm rounded-full px-2 py-0.5 sm:hidden"
                        >
                          <Sun className="w-3 h-3 text-white" />
                          <span className="text-xs font-medium text-white">{weather.temperature}°</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Ajuda/Help Button - hidden in voting mode */}
                    {!isVotingMode && (
                      <ScreenHelp 
                        screen="overview" 
                        className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
                      />
                    )}
                    
                    {/* Tutorial Button - always visible */}
                    <Button 
                      variant="secondary" 
                      size="sm"
                      className="rounded-full bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
                      onClick={() => setShowTripTutorial(true)}
                      data-testid="trip-help-btn"
                      title="Guia passo-a-passo"
                    >
                      <Sparkles className="w-4 h-4" />
                    </Button>
                    
                    {/* PDF Export Button - hidden in voting mode */}
                    {!isVotingMode && (
                      <Button 
                        variant="secondary" 
                        size="sm"
                        className="rounded-full bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
                        onClick={handleExportPDF}
                        data-testid="export-pdf-btn"
                      >
                        <Download className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">PDF</span>
                      </Button>
                    )}
                    
                    {/* Share Button - always visible */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm"
                          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white border-0"
                          data-testid="share-trip-btn"
                        >
                          <Share2 className="w-4 h-4 sm:mr-2" />
                          <span className="hidden sm:inline">{t('trips.invite')}</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('trips.inviteParticipants')}</DialogTitle>
                          <DialogDescription>
                            {t('trips.scanQRCode')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 space-y-4">
                          {/* QR Code */}
                          <div className="flex justify-center">
                            <div className="bg-white p-4 rounded-xl shadow-sm">
                              <QRCodeSVG 
                                value={`${PUBLIC_URL}/join/${currentTrip.invite_code}`}
                                size={180}
                                level="H"
                                includeMargin={true}
                                bgColor="#ffffff"
                                fgColor="#1e293b"
                              />
                            </div>
                          </div>
                          
                          {/* Invite Code */}
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Código</p>
                              <p className="text-xl font-mono font-bold tracking-widest text-slate-900 dark:text-white">
                                {currentTrip.invite_code}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={handleCopyCode}
                              data-testid="copy-code-btn"
                            >
                              {copied ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          
                          {/* Share Button */}
                          <Button
                            className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                            onClick={handleShare}
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            {t('trips.shareLink')}
                          </Button>
                          
                          <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                            A pessoa escaneará o QR Code e será levada para criar uma conta e entrar na viagem automaticamente
                          </p>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Settings Button - hidden in voting mode */}
                    {isOrganizer && !isVotingMode && (
                      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="sm"
                            className="rounded-full bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] p-0">
                          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                            <DialogTitle className="flex items-center gap-3 text-xl">
                              <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                <Settings className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                              </div>
                              <div>
                                <span className="block">{t('trips.tripSettings')}</span>
                                <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                                  {currentTrip.name}
                                </span>
                              </div>
                            </DialogTitle>
                            <DialogDescription>
                              Gerencie as configurações de transparência e lembretes automáticos desta viagem.
                            </DialogDescription>
                          </DialogHeader>
                          <ScrollArea className="max-h-[calc(90vh-140px)]">
                            <div className="p-6 space-y-6">
                              <TripGeneralSettings tripId={tripId} />
                              <TransparencySettings tripId={tripId} />
                              <RemindersManager tripId={tripId} />
                            </div>
                          </ScrollArea>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description (if exists) - Below banner - Hidden on mobile except on overview */}
            {currentTrip.description && (
              <div className={`px-4 sm:px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 ${activeTab !== '' ? 'hidden sm:block' : ''}`}>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{currentTrip.description}</p>
              </div>
            )}

            {/* Progress Line - Integrated into hero card - Hidden on mobile except on overview */}
            {!isVotingMode && startDate && endDate && (
              <div className={`px-4 sm:px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700 ${activeTab !== '' ? 'hidden sm:block' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {format(startDate, "dd MMM", { locale: ptBR })}
                  </span>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {(() => {
                      const daysLeft = differenceInDays(startDate, new Date());
                      if (daysLeft > 0) {
                        return `Faltam ${daysLeft} dias para a aventura! 🎉`;
                      } else if (daysLeft === 0) {
                        return 'A aventura começa hoje! 🚀';
                      } else {
                        const daysElapsed = Math.abs(daysLeft);
                        const totalDays = differenceInDays(endDate, startDate);
                        if (daysElapsed <= totalDays) {
                          return `Dia ${daysElapsed + 1} de ${totalDays + 1} da viagem ✈️`;
                        }
                        return 'Viagem concluída! 🏆';
                      }
                    })()}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {format(endDate, "dd MMM", { locale: ptBR })}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="relative h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ 
                      width: (() => {
                        const now = new Date();
                        if (now < startDate) return '0%';
                        if (now > endDate) return '100%';
                        const total = differenceInDays(endDate, startDate);
                        const elapsed = differenceInDays(now, startDate);
                        return `${Math.min(100, (elapsed / total) * 100)}%`;
                      })()
                    }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 to-sky-500 rounded-full"
                  />
                  {/* Current position indicator */}
                  <motion.div
                    initial={{ left: 0 }}
                    animate={{ 
                      left: (() => {
                        const now = new Date();
                        if (now < startDate) return '0%';
                        if (now > endDate) return '100%';
                        const total = differenceInDays(endDate, startDate);
                        const elapsed = differenceInDays(now, startDate);
                        return `${Math.min(100, (elapsed / total) * 100)}%`;
                      })()
                    }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white dark:bg-slate-200 border-4 border-orange-500 shadow-lg"
                    style={{ marginLeft: '-8px' }}
                  />
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Destination Voting - Show when in voting mode and on main tab */}
        {isVotingMode && currentTrip.destination_voting && activeTab === '' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <DestinationVoting trip={currentTrip} members={members} />
          </motion.div>
        )}

        {/* Activity Timeline - Show on main tab */}
        {activeTab === '' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <ActivityTimeline tripId={tripId} />
          </motion.div>
        )}

        {/* Desktop: Navigation Menu - Placeholder + Menu */}
        <div ref={menuPlaceholderRef} className={`hidden lg:block w-full ${isMenuSticky ? 'h-14' : ''}`}>
          <div 
            ref={menuRef}
            className={`transition-all duration-200 ${
              isMenuSticky 
                ? 'fixed top-16 left-64 right-4 z-40 py-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-md rounded-b-xl' 
                : ''
            }`}
          >
            {isMenuSticky ? (
              <Tabs value={activeTab} className="w-full">
                <TabsList className="flex flex-wrap gap-1 bg-transparent p-0 h-auto justify-center">
                  {menuItems
                    .filter(item => {
                      if (isVotingMode) return item.path === '' || item.path === 'members';
                      if (item.organizerOnly && !isOrganizer) return false;
                      if (item.completedOnly && currentTrip.status !== 'completed') return false;
                      return true;
                    })
                    .map((item) => (
                    <button 
                      key={item.path} 
                      onClick={() => navigate(`/trip/${tripId}/${item.path}`, { replace: false })}
                      className="cursor-pointer relative"
                    >
                      <TabsTrigger
                        value={item.path}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 data-[state=active]:bg-orange-500 data-[state=active]:text-white dark:data-[state=active]:bg-orange-500 transition-all hover:bg-slate-200 dark:hover:bg-slate-700 text-sm"
                        data-testid={`tab-${item.path || 'overview'}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                        {/* My Trip needs attention badge */}
                        {item.path === 'my-trip' && myTripNeedsAttention && (
                          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
                            !
                          </span>
                        )}
                      </TabsTrigger>
                    </button>
                  ))}
                </TabsList>
              </Tabs>
            ) : (
              <Card className="border-slate-100 dark:border-slate-800 shadow-sm">
                <CardContent className="p-2">
                  <Tabs value={activeTab} className="w-full">
                    <TabsList className="flex flex-wrap gap-1 bg-transparent p-0 h-auto justify-center">
                      {menuItems
                        .filter(item => {
                          if (isVotingMode) return item.path === '' || item.path === 'members';
                          if (item.organizerOnly && !isOrganizer) return false;
                          if (item.completedOnly && currentTrip.status !== 'completed') return false;
                          return true;
                        })
                        .map((item) => (
                        <button 
                          key={item.path} 
                          onClick={() => navigate(`/trip/${tripId}/${item.path}`, { replace: false })}
                          className="cursor-pointer relative"
                        >
                          <TabsTrigger
                            value={item.path}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 data-[state=active]:bg-orange-500 data-[state=active]:text-white dark:data-[state=active]:bg-orange-500 transition-all hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                            data-testid={`tab-${item.path || 'overview'}`}
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.label}</span>
                            {/* Pending count badge */}
                            {getPendingCount(item.path) > 0 && (
                              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-[10px] font-bold text-white">
                                {getPendingCount(item.path)}
                              </span>
                            )}
                            {/* My Trip needs attention badge */}
                            {item.path === 'my-trip' && myTripNeedsAttention && (
                              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
                                !
                              </span>
                            )}
                          </TabsTrigger>
                        </button>
                      ))}
                    </TabsList>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Mobile: Bottom Navigation Bar */}
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 safe-area-bottom">
          {/* Expanded menu overlay */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={() => setMobileMenuOpen(false)}
                />
                {/* Expanded menu */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.2 }}
                  className="absolute bottom-20 left-4 right-4 z-50 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-4"
                >
                  <div className="grid grid-cols-4 gap-2">
                    {menuItems
                      .filter(item => {
                        if (isVotingMode) return item.path === '' || item.path === 'members';
                        if (item.organizerOnly && !isOrganizer) return false;
                        if (item.completedOnly && currentTrip.status !== 'completed') return false;
                        return true;
                      })
                      .slice(4) // Skip the first 4 items (shown in bottom bar)
                      .map((item) => {
                        const isActive = activeTab === item.path;
                        const pendingCount = getPendingCount(item.path);
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              navigate(`/trip/${tripId}/${item.path}`);
                              setMobileMenuOpen(false);
                            }}
                            className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all ${
                              isActive
                                ? 'bg-orange-500 text-white'
                                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                            data-testid={`mobile-menu-${item.path || 'overview'}`}
                          >
                            <item.icon className="w-6 h-6" />
                            <span className="text-xs font-medium">{item.label}</span>
                            {pendingCount > 0 && (
                              <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-[10px] font-bold text-white">
                                {pendingCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Bottom navigation bar */}
          {(() => {
            // Filter menu items based on mode and permissions
            const filteredMenuItems = menuItems.filter(item => {
              if (isVotingMode) return item.path === '' || item.path === 'members';
              if (item.organizerOnly && !isOrganizer) return false;
              if (item.completedOnly && currentTrip.status !== 'completed') return false;
              return true;
            });
            
            // Check if there are extra items beyond the first 4
            const hasExtraItems = filteredMenuItems.length > 4;
            
            return (
              <div className="flex items-center justify-around px-1 py-2">
                {/* First 2 items */}
                {filteredMenuItems
                  .slice(0, 2)
                  .map((item) => {
                    const isActive = activeTab === item.path;
                    const pendingCount = getPendingCount(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(`/trip/${tripId}/${item.path}`)}
                        className={`relative flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg transition-all flex-1 max-w-[70px] ${
                          isActive
                            ? 'text-orange-500'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}
                        data-testid={`bottom-nav-${item.path || 'overview'}`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium truncate w-full text-center">
                          {item.label}
                        </span>
                        {pendingCount > 0 && (
                          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-[10px] font-bold text-white shadow-sm">
                            {pendingCount}
                          </span>
                        )}
                      </button>
                    );
                  })}

                {/* Center "+" button - only show if there are extra items */}
                {hasExtraItems && (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className={`flex items-center justify-center w-14 h-14 -mt-6 rounded-full shadow-lg transition-all ${
                      mobileMenuOpen 
                        ? 'bg-slate-700 dark:bg-slate-600 rotate-45' 
                        : 'bg-orange-500 hover:bg-orange-600'
                    }`}
                    data-testid="mobile-menu-toggle"
                  >
                    <Plus className="w-7 h-7 text-white" />
                  </button>
                )}

                {/* Next 2 items */}
                {filteredMenuItems
                  .slice(2, 4)
                  .map((item) => {
                    const isActive = activeTab === item.path;
                    const pendingCount = getPendingCount(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(`/trip/${tripId}/${item.path}`)}
                        className={`relative flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg transition-all flex-1 max-w-[70px] ${
                          isActive
                            ? 'text-orange-500'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}
                        data-testid={`bottom-nav-${item.path || 'overview'}`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium truncate w-full text-center">
                          {item.label}
                        </span>
                        {pendingCount > 0 && (
                          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-[10px] font-bold text-white shadow-sm">
                            {pendingCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            );
          })()}
        </div>

        {/* Info banner for voting mode - only show on main tab */}
        {isVotingMode && activeTab === '' && (
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-700 dark:text-purple-300 text-center">
              <Vote className="w-4 h-4 inline mr-2" />
              Esta viagem está em modo de votação. Roteiro, pagamentos e outras funcionalidades serão liberadas após escolher o destino.
            </p>
          </div>
        )}

        {/* Content Area - Add bottom padding on mobile for bottom nav */}
        <div className="pb-24 lg:pb-0">
          <Outlet context={{ trip: currentTrip, members, isOrganizer }} />
        </div>
      </div>

      {/* Trip Tutorial Modal */}
      <TripTutorial
        isOpen={showTripTutorial}
        onClose={() => setShowTripTutorial(false)}
        onComplete={() => setShowTripTutorial(false)}
        tripType={isVotingMode ? 'voting' : 'regular'}
        tripId={tripId}
      />
    </DashboardLayout>
  );
}
